/**
 * Operations Portal
 * 
 * For: Cat (operations, client_caregiver_services)
 * Focus: Clients, caregivers, scheduling, work queue
 * 
 * This is a shell for V1.0 - will be fully built in V1.1
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Briefcase, Users, Calendar, ClipboardList, Heart,
  AlertCircle, CheckCircle2, ArrowRight, Clock, Phone,
  MapPin, FileText, MessageSquare, Sparkles
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';

export default function OperationsPortal() {
  const { user } = useAuth();
  const firstName = user?.first_name || user?.name?.split(' ')[0] || 'there';
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-green-200 text-sm mb-2">
            <Briefcase className="h-4 w-4" />
            Operations Portal
          </div>
          <h1 className="text-2xl font-bold mb-1">Good morning, {firstName}</h1>
          <p className="text-green-100">Your daily operations at a glance</p>
        </div>
      </div>
      
      {/* Quick Stats */}
      <div className="max-w-7xl mx-auto px-6 -mt-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Today's Visits</p>
                  <p className="text-2xl font-bold">18</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Calendar className="h-5 w-5 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Clients</p>
                  <p className="text-2xl font-bold">24</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Heart className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Open Shifts</p>
                  <p className="text-2xl font-bold text-amber-600">2</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="shadow-lg">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Referrals</p>
                  <p className="text-2xl font-bold">3</p>
                </div>
                <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Work Queue */}
          <Card className="shadow-lg border-green-100">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardList className="h-5 w-5 text-green-500" />
                Today's Work Queue
              </CardTitle>
              <CardDescription>Tasks requiring your attention</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <AlertCircle className="h-5 w-5 text-amber-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">2 Open Shifts</p>
                  <p className="text-xs text-muted-foreground">Need coverage for tomorrow</p>
                </div>
                <Link to="/scheduler">
                  <Button size="sm" variant="outline">Fill</Button>
                </Link>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">3 New Referrals</p>
                  <p className="text-xs text-muted-foreground">Pending intake</p>
                </div>
                <Link to="/clients">
                  <Button size="sm" variant="outline">Review</Button>
                </Link>
              </div>
              <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium">All Visits On Track</p>
                  <p className="text-xs text-muted-foreground">No late arrivals today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Quick Links */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5 text-green-500" />
                Quick Access
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-3">
              <Link to="/clients">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Heart className="h-6 w-6 text-red-500 mb-2" />
                  <p className="font-medium text-sm">Clients</p>
                </div>
              </Link>
              <Link to="/scheduler">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Calendar className="h-6 w-6 text-blue-600 mb-2" />
                  <p className="font-medium text-sm">Scheduler</p>
                </div>
              </Link>
              <Link to="/intake-demo">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <Sparkles className="h-6 w-6 text-amber-600 mb-2" />
                  <p className="font-medium text-sm">Universal Intake</p>
                </div>
              </Link>
              <Link to="/records">
                <div className="p-4 rounded-lg border hover:bg-gray-50 transition-colors">
                  <FileText className="h-6 w-6 text-purple-600 mb-2" />
                  <p className="font-medium text-sm">Records</p>
                </div>
              </Link>
            </CardContent>
          </Card>
        </div>
        
        {/* Portal Shell Notice */}
        <Card className="mt-6 border-green-200 bg-green-50/50">
          <CardContent className="p-6 text-center">
            <Badge className="mb-4">V1.0 Preview</Badge>
            <h3 className="font-semibold text-lg mb-2">Operations Portal</h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Full operations dashboard with work queues, real-time visit tracking, 
              and communication center coming in V1.1. For now, use the sidebar 
              to access all platform features.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
